import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AnimatedFooterNavigation from '../../components/AnimatedFooterNavigation';
import useAutoSlide from '../../hooks/useAutoSlide';
import Icon from '../../components/AppIcon';
import { trackDemoMenuClick } from '../../utils/analytics';
import MobileDemoPhone from '../../components/MobileDemoPhone';

const PositionedMobileDemo = () => {
  const { t } = useTranslation();
  const [activeScreen, setActiveScreen] = useState('home');
  const [isAutoSlideEnabled, setIsAutoSlideEnabled] = useState(false);
  const sectionRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const hasSwipedRef = useRef(false);
  const [showGhost, setShowGhost] = useState(false);
  const [ghostPlayed, setGhostPlayed] = useState(false);
  const baseWidth = 280; // 데스크톱 내부 기준 폭
  const baseHeight = 600; // 데스크톱 내부 기준 높이
  const [isSmallScreen, setIsSmallScreen] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : true);
  const [isHeightSmall, setIsHeightSmall] = useState(false);
  const manualChangeTimeoutRef = useRef(null);

  // 화면 높이가 700px보다 작은지 체크
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const checkHeight = () => {
      setIsHeightSmall(window.innerHeight < 700);
    };

    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, []);

  // Define screens for auto-slide
  const screens = ['home', 'mingl', 'connect', 'talk', 'passport'];
  
  // Auto-slide hook with 5-second interval
  const {
    currentIndex,
    currentItem,
    isPlaying,
    goToSlide,
    play,
    pause,
    handleMouseEnter,
    handleMouseLeave,
    progress
  } = useAutoSlide(screens, 6000, false);

  // Sync active screen with auto-slide
  useEffect(() => {
    if (isAutoSlideEnabled && currentItem) {
      setActiveScreen(currentItem);
    }
  }, [currentItem, isAutoSlideEnabled]);

  // Handle manual screen change (pause auto-slide temporarily)
  const handleScreenChange = (screen) => {
    // Track menu click event
    trackDemoMenuClick(screen);
    
    setActiveScreen(screen);
    if (!ghostPlayed) { setShowGhost(false); setGhostPlayed(true); }
    const screenIndex = screens?.indexOf(screen);
    if (screenIndex >= 0) {
      goToSlide(screenIndex);
    }
    // Pause auto-slide temporarily when user manually selects
    pause();
    // Clear any existing timeout
    if (manualChangeTimeoutRef.current) {
      clearTimeout(manualChangeTimeoutRef.current);
    }
    // Resume auto-slide after 10 seconds
    manualChangeTimeoutRef.current = setTimeout(() => {
      if (isAutoSlideEnabled) {
        play();
      }
    }, 10000);
  };

  // Swipe-down gesture: step screens, after last allow/trigger scroll below
  const onTouchStart = (e) => {

    if (!ghostPlayed) { setShowGhost(false); setGhostPlayed(true); }
    const t = e.touches?.[0];
    if (!t) return;
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
    hasSwipedRef.current = false;
  };

  const onTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t || hasSwipedRef.current) return;
    const dx = t.clientX - touchStartXRef.current;
    const dy = t.clientY - touchStartYRef.current;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const threshold = 30;
    const isSmallScreen = typeof window !== 'undefined' ? window.innerWidth < 1024 : true;
    if (!isSmallScreen) return;
    if (absDy > threshold && absDy > absDx && dy > 0) {
      const screensArr = ['home', 'mingl', 'connect', 'talk', 'passport'];
      const currentIdx = screensArr.indexOf(activeScreen);
      if (currentIdx < screensArr.length - 1) {
        e.preventDefault();
        hasSwipedRef.current = true;
        handleScreenChange(screensArr[currentIdx + 1]);
      } else {
        hasSwipedRef.current = true;
        requestAnimationFrame(() => {
          const rect = sectionRef.current?.getBoundingClientRect?.();
          const offset = (rect ? rect.bottom + window.scrollY : window.scrollY + window.innerHeight * 0.8) + 1;
          window.scrollTo({ top: offset, behavior: 'smooth' });
        });
      }
    }
  };

  const onTouchEnd = () => {
    // no-op; reserved for future use
  };


  // Detect screen size for scale adjustment
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // 초기 실행
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show ghost hand after 4s of inactivity (once)
  useEffect(() => {
    if (ghostPlayed) return;
    const timer = setTimeout(() => setShowGhost(true), 4000);
    const cancel = () => {
      clearTimeout(timer);
      setShowGhost(false);
      setGhostPlayed(true);
    };
    window.addEventListener('scroll', cancel, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', cancel);
    };
  }, [ghostPlayed]);

  // Cleanup manual change timeout on unmount
  useEffect(() => {
    return () => {
      if (manualChangeTimeoutRef.current) {
        clearTimeout(manualChangeTimeoutRef.current);
      }
    };
  }, []);

  // Enable auto-slide when section is visible in viewport
  useEffect(() => {
    const checkVisibility = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // 섹션이 뷰포트에 보이는지 확인 (상단이 화면 상단보다 위에 있고, 하단이 화면 하단보다 아래에 있으면 보이는 것으로 간주)
      const isVisible = rect.top < windowHeight * 0.8 && rect.bottom > windowHeight * 0.2;
      
      if (isVisible && !isAutoSlideEnabled) {
        setIsAutoSlideEnabled(true);
        // 약간의 지연 후 play() 호출하여 hook이 준비되도록 함
        setTimeout(() => {
          play();
        }, 100);
      } else if (!isVisible && isAutoSlideEnabled) {
        setIsAutoSlideEnabled(false);
        pause();
      }
    };

    // 초기 실행 (약간의 지연을 두어 DOM이 준비되도록 함)
    const initialCheck = setTimeout(() => {
      checkVisibility();
    }, 100);
    
    window.addEventListener('scroll', checkVisibility, { passive: true });
    window.addEventListener('resize', checkVisibility, { passive: true });
    
    return () => {
      clearTimeout(initialCheck);
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }, [isAutoSlideEnabled, play, pause]);

  // Apply CSS Scroll Snap to body/html only when near this section
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 1024 : true;
    if (!isMobile) return;

    const html = document.documentElement;
    let scrollSnapEnabled = false;
    
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const sectionTop = sectionRef.current.offsetTop;
      
      // 섹션에 접근 중일 때 (섹션 위쪽 1.5배 화면 높이 이내)
      const approachingSection = scrollY >= sectionTop - windowHeight * 1.5 && scrollY < sectionTop + windowHeight * 0.5;
      
      // 섹션을 지나갔을 때 (섹션 하단을 지나감)
      const passedSection = scrollY > sectionTop + sectionRef.current.offsetHeight;
      
      if (approachingSection && !passedSection) {
        // 섹션 근처에 있을 때만 scroll snap 적용
        if (!scrollSnapEnabled) {
          html.style.scrollSnapType = 'y proximity'; // mandatory 대신 proximity 사용 (덜 강제적)
          html.style.scrollBehavior = 'smooth';
          scrollSnapEnabled = true;
        }
      } else {
        // 섹션을 지나갔거나 멀리 떨어져 있으면 scroll snap 제거
        if (scrollSnapEnabled) {
          html.style.scrollSnapType = '';
          html.style.scrollBehavior = '';
          scrollSnapEnabled = false;
        }
      }
    };

    // 초기 실행
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      html.style.scrollSnapType = '';
      html.style.scrollBehavior = '';
    };
  }, []);

  // Screen titles and descriptions for the description section (not used in MobileDemoPhone)
  const screenInfo = {
    home: {
      title: 'home',
      description: t('positionedMobileDemo.home.description'),
      features: [
        {
          icon: 'Newspaper',
          description: t('positionedMobileDemo.home.features.0.description'),
        },
        {
          icon: 'MapPin',
          description: t('positionedMobileDemo.home.features.1.description'),
        },
        {
          icon: 'Calendar',
          description: t('positionedMobileDemo.home.features.2.description'),
        },
      ],
    },
    mingl: {
      title: 'mingl',
      description: t('positionedMobileDemo.mingl.description'),
      features: [
        {
          icon: 'Handshake',
          description: t('positionedMobileDemo.mingl.features.0.description'),
        },
        {
          icon: 'Globe',
          description: t('positionedMobileDemo.mingl.features.1.description'),
        },
        {
          icon: 'Heart',
          description: t('positionedMobileDemo.mingl.features.2.description'),
        },
      ],
    },
    connect: {
      title: 'connect',
      description: t('positionedMobileDemo.connect.description'),
      features: [
        {
          icon: 'Repeat', // 교류(Exchange/Interaction) 느낌 강조, Handshake/Share2/Users2 제외
          description: t('positionedMobileDemo.connect.features.0.description'),
        },
        {
          icon: 'Search',
          description: t('positionedMobileDemo.connect.features.1.description'),
        },
        {
          icon: 'Users',
          description: t('positionedMobileDemo.connect.features.2.description'),
        },
      ],
    },
    talk: {
      title: 'talk',
      description: t('positionedMobileDemo.talk.description'),
      features: [
        {
          icon: 'Languages',
          description: t('positionedMobileDemo.talk.features.0.description'),
        },
        {
          icon: 'MessageCircle',
          description: t('positionedMobileDemo.talk.features.1.description'),
        },
        {
          icon: 'ArrowRight',
          description: t('positionedMobileDemo.talk.features.2.description'),
        },
      ],
    },
    passport: {
      title: 'passport',
      description: t('positionedMobileDemo.passport.description'),
      features: [
        {
          icon: 'Clock',
          description: t('positionedMobileDemo.passport.features.0.description'),
        },
        {
          icon: 'Star',
          description: t('positionedMobileDemo.passport.features.1.description'),
        },
        {
          icon: 'Link',
          description: t('positionedMobileDemo.passport.features.2.description'),
        },
      ],
    }
  };

  // Remove unused screenContent - now using MobileDemoPhone component

  return (
    <section 
      id="demo-section" 
      className="h-screen flex flex-col py-8 sm:pt-24"
      style={{ 
        scrollSnapAlign: 'start', 
        scrollSnapStop: 'always',
        scrollMarginTop: '0'
      }}
      ref={sectionRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col justify-between flex-1">
        {/* Section Header */}

        <div className="text-center">
          <motion.h2 
            className="text-center"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeScreen}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
                className="text-2xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-2"
              >
                {screenInfo[activeScreen]?.title || 'home'}
              </motion.div>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.div
                key={`desc-${activeScreen}`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
                className="text-sm sm:text-base lg:text-lg text-gray-600 mt-2"
              >
                {screenInfo[activeScreen]?.description || ''}
              </motion.div>
            </AnimatePresence>
          </motion.h2>
        </div>

        {/* Mobile: Beta 라벨 (폰프레임 위쪽, 소형 화면 전용) */}
        {/* 2026-06-18 [YJ] 비활성화: "Beta launching Q1 2025 in Seoul" 숨김 (원복하려면 false 제거) */}
        {false && (
        <div className="flex sm:hidden items-center justify-center space-x-2 text-sm text-red-500 my-3">
          <Icon name="CheckCircle" size={16} className="text-red-500" />
          <span>Beta launching Q1 2025 in Seoul</span>
        </div>
        )}

        {/* Tablet: Beta 라벨을 제목/설명과 폰프레임 사이의 중간 영역에 배치 */}
        {/* 2026-06-18 [YJ] 비활성화: "Beta launching Q1 2025 in Seoul" 숨김 (원복하려면 false 제거) */}
        {false && (
        <div className="hidden sm:flex lg:hidden items-center justify-center space-x-2 text-sm sm:text-base text-red-500 my-4 sm:my-6 md:my-8">
          <Icon name="CheckCircle" size={16} className="text-red-500" />
          <span>Beta launching Q1 2025 in Seoul</span>
        </div>
        )}
        {/* Demo Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Mobile Mockup - 폰 프레임 축소 및 내부 스크린 스케일 조정 */}
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="relative">
              {/* Phone Frame (desktop style, size unchanged) */}
              <div
                className="relative w-60 h-[500px] sm:w-[308px] sm:h-[660px] rounded-[2.75rem] p-[3px] shadow-demo"
                style={{ background: 'linear-gradient(to bottom right, #3a3d2f, #2c2e24, #1f2019)' }}
              >
                {/* Home Indicator */}
                <div
                  className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-20 h-1 rounded-full"
                  style={{ backgroundColor: '#4a4d3f' }}
                ></div>

                {/* Phone Body - MobileDemoPhone component */}
                <div className="w-full h-full rounded-[2.7rem] relative" style={{ overflow: 'hidden' }}>
                  {/* Anchor button at phone top-center */}
                  <div 
                    className="absolute inset-0"
                    style={isSmallScreen ? { 
                      transform: 'scale(0.857)',
                      transformOrigin: 'top left',
                      width: '116.67%',
                      height: '116.67%'
                    } : {}}
                  >
                    <MobileDemoPhone activeScreen={activeScreen} onScreenChange={handleScreenChange} />
                  </div>
                  {/* Ghost hand gesture: start bottom-right, move to talk tab, tap ripple, stop */}
                  {showGhost && !ghostPlayed && (
                    <motion.div
                      className="absolute z-30"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        initial={{ x: baseWidth * 0.72 * 0.857, y: baseHeight * 0.68 * 0.857 }}
                        animate={{ x: baseWidth * 0.58 * 0.857, y: baseHeight * 0.88 * 0.857 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        onAnimationComplete={() => {
                          setTimeout(() => { setShowGhost(false); setGhostPlayed(true); }, 300);
                        }}
                      >
                        <div className="relative">
                          <div className="w-5 h-5 rounded-full bg-white shadow border border-gray-300" />
                          <motion.div
                            className="absolute inset-0 rounded-full border-2 border-orange-400"
                            initial={{ opacity: 0, scale: 1 }}
                            animate={{ opacity: [0, 1, 0], scale: [1, 1.3, 1.8] }}
                            transition={{ duration: 0.12, delay: 0.2, ease: 'easeOut' }}
                          />
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

        </div>

          {/* Content Description */}
          {!isHeightSmall && (
            <motion.div 
              className="space-y-6 text-center flex flex-col items-center"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeScreen}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
              </motion.div>
            </AnimatePresence>

            {/* Feature Highlights - Dynamic based on active screen */}
            <AnimatePresence mode="wait">
              <motion.div 
                key={`features-${activeScreen}`}
                className="space-y-4 w-fit"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {screenInfo[activeScreen]?.features?.map((feature, index) => {
                  const iconColors = ['text-primary', 'text-accent', 'text-blue-500'];
                  return (
                    <motion.div 
                      key={index}
                      className="flex items-center space-x-3" 
                      whileHover={{ x: 5 }}
                    >
                      <Icon name={feature.icon} size={20} className={iconColors[index % 3]} />
                      <span className="text-[12px] sm:text-sm text-text-secondary">{feature.description}</span>
                    </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>

          </motion.div>
          )}
      </div>
    </section>
  );
};

export default PositionedMobileDemo;